// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the server list for moving an emoji (emoji-plan.md section 11), a modal opened from the emoji row's
// move button (beside its delete button, at every width). Picking a server moves the emoji at once. As Obyrus's sheet,
// the title and hint stay put and the list scrolls in the rest of the height. Exclude from upstream sync.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/emoji/components/emojis/EmojiMoveDrawer.module.css';
import {
	canMoveEmoji,
	emojiMoveTargets,
	MOVE_EMOJI_FAILED_DESCRIPTOR,
	MOVE_EMOJI_NAME_CLASH_HINT_DESCRIPTOR,
	MOVE_EMOJI_NO_SERVERS_DESCRIPTOR,
	MOVE_EMOJI_NO_SLOTS_DESCRIPTOR,
	MOVE_TO_SERVER_DESCRIPTOR,
	MOVED_TO_SERVER_DESCRIPTOR,
	moveEmoji,
} from '@app/features/expressions/utils/EmojiMove';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const logger = new Logger('EmojiMove');

const canManageExpressionsIn = (guildId: string) => Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId});

export function emojiMoveServers(guildId: string) {
	return emojiMoveTargets(Guilds.getGuilds(), canManageExpressionsIn, guildId);
}

/** The move rule for an emoji of this server (emoji-plan.md section 14), from the guild and permission stores. */
export function canMoveEmojiFrom(guildId: string): boolean {
	return canMoveEmoji(guildId, Guilds.getGuilds(), canManageExpressionsIn);
}

/** Moves the emoji and tells the user how it went (toast); true when it moved. */
export async function moveEmojiTo(
	i18n: I18n,
	guildId: string,
	emojiId: string,
	target: {id: string; name: string},
): Promise<boolean> {
	try {
		await moveEmoji(guildId, emojiId, target.id);
		ToastCommands.createToast({type: 'success', children: i18n._(MOVED_TO_SERVER_DESCRIPTOR, {server: target.name})});
		return true;
	} catch (error) {
		logger.error('Failed to move emoji', error);
		const noSlots = failureCode(error) === APIErrorCodes.MAX_EMOJIS;
		ToastCommands.createToast({
			type: 'error',
			children: noSlots
				? i18n._(MOVE_EMOJI_NO_SLOTS_DESCRIPTOR, {server: target.name})
				: i18n._(MOVE_EMOJI_FAILED_DESCRIPTOR),
		});
		return false;
	}
}

/** The server list as a modal. */
export function openEmojiMoveDrawer(guildId: string, emojiId: string, onMoved: () => void): void {
	ModalCommands.push(
		modal(() => (
			<EmojiMoveModal
				guildId={guildId}
				emojiId={emojiId}
				onMoved={onMoved}
				data-flx="emoji.emojis.emoji-move-drawer.modal"
			/>
		)),
	);
}

const EmojiMoveModal = observer(function EmojiMoveModal({
	guildId,
	emojiId,
	onMoved,
}: {
	guildId: string;
	emojiId: string;
	onMoved: () => void;
}) {
	const {i18n} = useLingui();
	const servers = emojiMoveServers(guildId);
	return (
		<Modal.Root size="small" centered data-flx="emoji.emojis.emoji-move-drawer.root">
			<Modal.Header title={i18n._(MOVE_TO_SERVER_DESCRIPTOR)} data-flx="emoji.emojis.emoji-move-drawer.header">
				<p className={styles.hint} data-flx="emoji.emojis.emoji-move-drawer.hint">
					{i18n._(MOVE_EMOJI_NAME_CLASH_HINT_DESCRIPTOR)}
				</p>
			</Modal.Header>
			<Modal.Content data-flx="emoji.emojis.emoji-move-drawer.content">
				{servers.length === 0 ? (
					<p className={styles.noServers} data-flx="emoji.emojis.emoji-move-drawer.no-servers">
						{i18n._(MOVE_EMOJI_NO_SERVERS_DESCRIPTOR)}
					</p>
				) : (
					<div className={styles.list} data-flx="emoji.emojis.emoji-move-drawer.list">
						{servers.map((server) => (
							<button
								key={server.id}
								type="button"
								className={styles.server}
								onClick={() => {
									ModalCommands.pop();
									void moveEmojiTo(i18n, guildId, emojiId, server).then((moved) => moved && onMoved());
								}}
								data-flx="emoji.emojis.emoji-move-drawer.server"
							>
								{server.name}
							</button>
						))}
					</div>
				)}
			</Modal.Content>
		</Modal.Root>
	);
});
